import React, { ChangeEvent, ReactElement, useContext, useEffect, useState } from "react";
import { Button, Icon, Switch, Text } from "@jarvis-catalyst/ui-components";
import { OPTInList } from "../../../utils/listOptions";
import { ConsentChildrenType, ConstantValuesToGroup } from "../../../types";
import { UseFormReturn } from "react-hook-form";
import ConsentContext from "../../../context/ConsentContext";
import { UpdateAndPreviewPdf } from "../uploadAndPreviewPdf";
import { openPdfInNewTab } from "../../../utils/processPdfDocument";
import { CMRPUNTOS_DESCRIPTION, GCP_BUCKET_PDF_URL } from "../../../config/constants";

type Props = {
  consent: ConsentChildrenType;
  position: number;
  useStateForm: UseFormReturn<ConstantValuesToGroup, any>;
};

const Consent = ({ consent, position = 1, useStateForm }: Props): ReactElement => {
  const { positionsIntoGrup, moveElementInGroup } = useContext(ConsentContext);

  const [explicitAgree, setExplicitAgree] = useState(consent.explicitAgree ?? false);

  const [pdfViewerAttributes, setPdfViewerAttributes] = useState({
    title: "",
    content: ""
  });

  const { formState, setValue } = useStateForm;

  const keyTemplate = `template-${position}`;

  useEffect(() => {
    setValue(`${keyTemplate}.position`, position);
    setValue(`${keyTemplate}.group`, Number(consent.group));
    setValue(`${keyTemplate}.simpleText`, consent.simpleText);
    setValue(`${keyTemplate}.displayText`, consent.displayText);
    setValue(`${keyTemplate}.mandatory`, consent.mandatory);
    setValue(`${keyTemplate}.code`, consent.code);
    setValue(`${keyTemplate}.version`, consent.version);
    setValue(`${keyTemplate}.legalText`, consent.legalText);
    setValue(`${keyTemplate}.explicitAgree`, explicitAgree);
    setValue(`${keyTemplate}.legalTextPath`, consent.legalTextPath ?? "");
  });

  const handleTextLegalPreviewNewVersion = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();
    openPdfInNewTab(`${GCP_BUCKET_PDF_URL}/${consent.legalTextPath}`);
  };

  return (
    <>
      <div className="col-sm-2 d-flex justify-content-center">
        <select
          disabled={["loyalty_rule", "policy_rule"].includes(consent.name)}
          className="form-select"
          {...useStateForm.register(`${keyTemplate}.position`, {
            value: position,
            required: true,
            onChange: (e: ChangeEvent<HTMLSelectElement>) => {
              const lastPosition = consent.position;
              moveElementInGroup(consent.group, lastPosition, Number(e.target.value));

              setValue(`${keyTemplate}.position`, Number(e.target.value));
            }
          })}
        >
          {positionsIntoGrup(Number(consent.group))?.map((item, index) => (
            <option key={index} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      <div className="col-sm-10 row mt-4 mb-4">
        <div className="mb-2">
          <Text className="">Categoría OPT-IN</Text>
          <select
            {...useStateForm.register(`${keyTemplate}.simpleText`, {
              required: true,
              onChange: (e: any) => {
                consent.simpleText = e.target.value;
              }
            })}
            className={`form-control ${formState.errors[`${keyTemplate}`]?.simpleText ? "is-invalid" : ""}`}
            disabled={!consent.editable}
          >
            <option value="">Seleccione una Categoría OPT-IN</option>
            {OPTInList().map((item, index) => (
              <option key={index} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mb-2">
          <div className="row">
            <Text className="">Texto Checkbox</Text>
            <div className="col-sm-10">
              <input
                disabled={!consent.editable}
                {...useStateForm.register(`${keyTemplate}.displayText`, {
                  required: true,
                  onChange: (e: any) => {
                    consent.displayText = e.target.value;
                  }
                })}
                className={`form-control ${
                  formState.errors[`${keyTemplate}`]?.displayText ? "is-invalid" : ""
                }`}
                type="text"
              />
            </div>
          </div>
        </div>
        <div className="mb-5">
          <div className="row">
            <div className="col-sm-3">
              <Text>Obligatoriedad</Text>
              <select
                disabled={!consent.editable}
                className="form-select"
                {...useStateForm.register(`${keyTemplate}.mandatory`, {
                  required: true,
                  onChange: (e: any) => {
                    consent.mandatory = e.target.value;
                  }
                })}
              >
                <option value={"no"}>No</option>
                <option value={"yes"}>Sí</option>
              </select>
            </div>
            <div className="col-sm-6">
              <Text className="">Código del Texto legal</Text>
              <input
                disabled={!consent.editable}
                type="text"
                className={`form-control ${formState.errors[`${keyTemplate}`]?.code ? "is-invalid" : ""}`}
                {...useStateForm.register(`${keyTemplate}.code`, {
                  required: true,
                  onChange(event) {
                    consent.code = event.target.value;
                  }
                })}
              />
            </div>
            <div className="col-sm-2">
              <Text>Versión</Text>
              <input
                disabled={!consent.editable}
                type="text"
                className={`form-control ${formState.errors[`${keyTemplate}`]?.version ? "is-invalid" : ""}`}
                {...useStateForm.register(`${keyTemplate}.version`, {
                  required: true,
                  onChange(event) {
                    consent.version = event.target.value;
                  }
                })}
              />
            </div>
          </div>
        </div>
        <div className="mb-5">
          <div className="row">
            <div className="col-sm-6" style={{ marginTop: "-30px", marginBottom: "-15px" }}>
              <Text>Aceptación Explicita</Text>
              <Switch
                onChange={(value) => {
                  setExplicitAgree(value);
                  consent.explicitAgree = value;
                  setValue(`${keyTemplate}.explicitAgree`, value, { shouldValidate: false });
                }}
                value={consent.explicitAgree}
                size="sm"
              />
            </div>
          </div>
        </div>
        <div style={{ marginTop: "-30px" }}>
          <div className="row">
            <Text>Texto Legal</Text>
            {consent.code?.toLowerCase().includes(CMRPUNTOS_DESCRIPTION.TYC_CMR_PUNTOS) ||
            consent.code?.toLowerCase().includes(CMRPUNTOS_DESCRIPTION.PDP_CMR_PUNTOS) ? (
              <>
                <div className="col-sm-10">
                  <textarea className="form-control" disabled value={consent.legalTextPath} />
                </div>
                <div className="col-sm-2 d-flex align-items-center">
                  <Button
                    onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                      handleTextLegalPreviewNewVersion(e)
                    }
                  >
                    <Icon name="View" color="white" size={24} />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <UpdateAndPreviewPdf
                  consent={consent}
                  pdfViewerAttributes={pdfViewerAttributes}
                  setPdfViewerAttributes={setPdfViewerAttributes}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Consent;
